// Libraries
import { Grid } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useMemo } from "react";

// Components
import ProspectInformationSection from "./ProspectInformationSection";
import SectionDivider from "../../SectionDivider";
import AdditionalDetailsSection from "./AdditionalDetailsSection";

// Context
import { useLeadsContext } from "@/DataPages/context/crm/LeadsContext";

// Helpers
import { getPositionsByCategory, getUserOptions } from "../../../helpers";

export default function ProspectInformationTab() {
	/* Context */
	const { leadInputForm, getContextValue } = useLeadsContext();

	/* Global state */
	const locationInfo = useSelector((state: any) => state.location.location);

	/* useMemos */
	const { franchises, BDAs, locationId } = useMemo(() => {
		const { franchises, oems, locationId } = locationInfo;
		const BDAs = oems?.map((oem: { dba: string }) => ({ type: oem.dba })) ?? [];

		return { franchises, BDAs, locationId };
	}, [locationInfo]);

	const positionsByCategory = useMemo(() => {
		const category = leadInputForm.prospect.category || "";
		return getPositionsByCategory(category);
	}, [leadInputForm.prospect.category]);

	/* Queries */
	const [
		{ data: salesPersonOptions = [], isLoading: salesPersonLoading },
		{ data: bdcUsersOptions = [], isLoading: bdcUsersLoading },
		{ data: managerUsersOptions = [], isLoading: managerUsersLoading },
	] = useQueries({
		queries: [
			{
				queryKey: ["salesPersonOptions", locationId, getContextValue("prospect", "category")],
				queryFn: async () => getUserOptions(locationId, positionsByCategory?.sales),
				enabled: Boolean(locationId) && Boolean(positionsByCategory?.sales?.length),
			},
			{
				queryKey: ["bdcUsersOptions", locationId, getContextValue("prospect", "category")],
				queryFn: async () => getUserOptions(locationId, positionsByCategory?.bdc),
				enabled: Boolean(locationId) && Boolean(positionsByCategory?.bdc?.length),
			},
			{
				queryKey: ["managerUsersOptions", locationId, getContextValue("prospect", "category")],
				queryFn: async () => getUserOptions(locationId, positionsByCategory?.manager),
				enabled: Boolean(locationId) && Boolean(positionsByCategory?.manager?.length),
			},
		],
	});

	return (
		<Grid className="!w-full">
			<ProspectInformationSection
				salesPersonData={{ data: salesPersonOptions, loading: salesPersonLoading }}
				franchises={franchises}
				BDAs={BDAs}
			/>
			<SectionDivider />
			<AdditionalDetailsSection
				salesPersonData={{ data: salesPersonOptions, loading: salesPersonLoading }}
				bdcUsersData={{ data: bdcUsersOptions, loading: bdcUsersLoading }}
				managerUsersData={{ data: managerUsersOptions, loading: managerUsersLoading }}
			/>
		</Grid>
	);
}
